import React, { useState, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMobileAlt,
    faCommentDots,
    faEye,
    faUserCheck,
    faTriangleExclamation,
    faExternalLinkAlt,
    faClock,
    faRotateRight,
    faTag,
    faLayerGroup,
    faCalendarCheck,
    faReply,
    faUser
} from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import { formatRelativeTime, getTicketInitials, getTicketStatusInfo, getTicketUrgencyInfo, formatDurationMinutes, formatDateTime } from '../utils';
import { formatWhatsAppText } from '../../TicketDetailView/utils';

const TicketCardItem = ({
    ticket,
    onSelectTicket,
    onAssignTicketAgent,
    agents = []
}) => {
    const { config: { isDark } = {} } = useContext(AppContext);
    const [, setCurrentTime] = useState(() => Date.now());

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(Date.now());
        }, 60000);
        return () => clearInterval(timer);
    }, []);

    const ticketId = ticket.idTicket || ticket.id;
    const ticketUrl = `/support-engine/ticket/${ticketId}`;
    const contactName = ticket.vNombreContacto?.trim() || ticket.contactName?.trim();
    const username = ticket.vUsername?.trim() || ticket.username?.trim();
    const clientName = ticket.vCustomerName?.trim()
        || contactName
        || (username ? `@${username}` : null)
        || ticket.vTelefono
        || 'Cliente';
    const phone = ticket.vTelefono || (username ? `@${username}` : ticket.vFromUserId || 'Sin teléfono');
    const isPhone = Boolean(ticket.vTelefono);
    const lastTime = formatRelativeTime(ticket.dUltimaActividad || ticket.dCreatedDate);
    const isPending = Number(ticket.iEstadoTicket || ticket.status) === 1;
    const isResolved = Number(ticket.iEstadoTicket || ticket.status) === 2;
    const isInProgress = Number(ticket.iEstadoTicket || ticket.status) === 3;

    const startDate = isPending
        ? (ticket.dCreatedDate || ticket.createdDate)
        : (ticket.dFechaRespuestaInicial || ticket.dCreatedDate || ticket.createdDate);

    const { timeFormatted, timeClass } = getTicketUrgencyInfo(
        startDate,
        ticket.iEstadoTicket || ticket.status
    );

    const resolvedTime = isResolved ? formatDurationMinutes(ticket.iTiempoResolucionMinutos) : null;
    const firstResponseTime = isResolved ? formatDurationMinutes(ticket.iTiempoRespuestaMinutos) : null;
    const closedDateTime = isResolved && ticket.dFechaCierre ? formatDateTime(ticket.dFechaCierre) : null;

    const statusInfo = getTicketStatusInfo(ticket.iEstadoTicket || ticket.status);
    const initials = getTicketInitials(clientName, phone);

    const hasAssignedAgent = Boolean(ticket.idUsuarioAsignado && Number(ticket.idUsuarioAsignado) > 0);
    const assignedAgent = hasAssignedAgent ? agents?.find(a => Number(a.idUsuario) === Number(ticket.idUsuarioAsignado)) : null;
    const rawAgentName = (assignedAgent?.vNombreCompleto || assignedAgent?.vNombre || ticket.vNombreUsuarioAsignado || '').trim();
    const assignedAgentName = hasAssignedAgent && rawAgentName ? rawAgentName : null;

    const handleCardClick = e => {
        if (e.target.closest('button') || e.target.closest('a')) {
            return;
        }
        onSelectTicket(ticket);
    };

    const handleCardAuxClick = e => {
        if (e.button === 1) {
            if (e.target.closest('button') || e.target.closest('a')) {
                return;
            }
            window.open(ticketUrl, '_blank');
        }
    };

    const isUnread = Boolean(ticket.bSinLeer) && !isPending;

    const statusBg = isDark
        ? (isPending
            ? 'rgba(245, 158, 11, 0.18)'
            : Number(ticket.iEstadoTicket) === 3
                ? 'rgba(14, 165, 233, 0.18)'
                : 'rgba(16, 185, 129, 0.18)')
        : statusInfo.bgColor;

    const statusColor = isDark
        ? (isPending
            ? '#fbbf24'
            : Number(ticket.iEstadoTicket) === 3
                ? '#38bdf8'
                : '#34d399')
        : statusInfo.textColor;

    return (
        <div
            onClick={handleCardClick}
            onAuxClick={handleCardAuxClick}
            className="border rounded-3 transition-base cursor-pointer hover-shadow-sm position-relative overflow-hidden mb-2"
            style={{
                borderLeft: isUnread ? '4px solid #25d366' : `4px solid ${statusInfo.accentColor}`,
                backgroundColor: isDark ? (isUnread ? '#07241c' : '#121e2d') : (isUnread ? '#f8fdf9' : '#ffffff'),
                borderColor: isDark ? (isUnread ? '#059669' : '#233142') : (isUnread ? '#a7f3d0' : '#eef2f6'),
                boxShadow: isUnread ? '0 2px 10px rgba(37, 211, 102, 0.12)' : 'none',
                zIndex: 1
            }}
        >
            <div className="p-2 p-md-3">
                {/* === HEADER EN MÓVIL (Avatar + Info rápida) === */}
                <div className="d-flex d-md-none align-items-center justify-content-between mb-2 pb-2 border-bottom border-translucent">
                    <div className="d-flex align-items-center gap-2 min-w-0">
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                                width: '34px',
                                height: '34px',
                                backgroundColor: isUnread ? (isDark ? '#064e3b' : '#ecfdf5') : statusBg,
                                color: isUnread ? (isDark ? '#34d399' : '#059669') : statusColor,
                                fontWeight: 700,
                                fontSize: '0.8rem',
                                border: isUnread ? '2px solid #34d399' : (isDark ? '1px solid #233142' : 'none')
                            }}
                        >
                            {initials}
                        </div>
                        <div className="min-w-0">
                            <div className="d-flex align-items-center gap-1">
                                <span className="fw-bold text-truncate" style={{ fontSize: '0.9rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                                    {clientName}
                                </span>
                                {contactName && !ticket.idClienteAkasia && (
                                    <span
                                        className="badge px-1 py-0 flex-shrink-0"
                                        style={{
                                            fontSize: '0.62rem',
                                            backgroundColor: isDark ? 'rgba(37, 211, 102, 0.15)' : '#ecfdf5',
                                            color: isDark ? '#34d399' : '#059669',
                                            border: isDark ? '1px solid rgba(52, 211, 153, 0.3)' : '1px solid #a7f3d0'
                                        }}
                                    >
                                        WhatsApp
                                    </span>
                                )}
                            </div>
                            {username && contactName && (
                                <span className="fw-normal text-muted d-block" style={{ fontSize: '0.78rem' }}>
                                    (@{username})
                                </span>
                            )}
                            <span className="text-muted d-block" style={{ fontSize: '0.72rem' }}>
                                #TCK-{ticketId}
                            </span>
                        </div>
                    </div>
                    <span
                        className="px-2 py-1 rounded flex-shrink-0"
                        style={{
                            backgroundColor: statusBg,
                            color: statusColor,
                            fontSize: '0.68rem',
                            fontWeight: 600
                        }}
                    >
                        {statusInfo.label}
                    </span>
                </div>

                {/* === ESTRUCTURA PRINCIPAL (DESKTOP & CONTINUACIÓN MÓVIL) === */}
                <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">

                    {/* 1. Columna Avatar (Solo Desktop) */}
                    <div
                        className="d-none d-md-flex flex-column align-items-center justify-content-center flex-shrink-0"
                        style={{ width: '90px' }}
                    >
                        <div className="position-relative mb-2">
                            <div
                                className="rounded-circle d-flex align-items-center justify-content-center shadow-xs"
                                style={{
                                    width: '42px',
                                    height: '42px',
                                    backgroundColor: isUnread ? (isDark ? '#064e3b' : '#ecfdf5') : statusBg,
                                    color: isUnread ? (isDark ? '#34d399' : '#059669') : statusColor,
                                    fontWeight: 700,
                                    fontSize: '0.9rem',
                                    border: isUnread ? '2px solid #34d399' : (isDark ? '1px solid #233142' : 'none')
                                }}
                            >
                                {initials}
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-1">
                            <span
                                className="px-2 py-1 rounded"
                                style={{
                                    backgroundColor: statusBg,
                                    color: statusColor,
                                    fontSize: '0.7rem',
                                    fontWeight: 600
                                }}
                            >
                                {statusInfo.label}
                            </span>
                            {statusInfo.hasWarning && (
                                <FontAwesomeIcon
                                    icon={faTriangleExclamation}
                                    style={{ color: '#ef4444', fontSize: '0.8rem' }}
                                />
                            )}
                        </div>
                    </div>

                    {/* 2. Columna Contenido Central */}
                    <div className="flex-grow-1 min-w-0 ps-md-2">
                        {/* Header Desktop (Cliente y badges) */}
                        <div className="d-none d-md-flex align-items-center gap-2 mb-1 flex-wrap">
                            {ticket.idClienteAkasia ? (
                                <a
                                    href={`/clientes/detalle/${ticket.idClienteAkasia}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={e => e.stopPropagation()}
                                    className="fw-bold text-decoration-none d-inline-flex align-items-center gap-1"
                                    style={{ fontSize: '0.98rem', color: isDark ? '#38bdf8' : '#0284c7' }}
                                    title={`Ver perfil del cliente #${ticket.idClienteAkasia}`}
                                >
                                    <span style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>{clientName}</span>
                                    <FontAwesomeIcon icon={faExternalLinkAlt} className="ms-1" style={{ fontSize: '0.7rem', opacity: 0.7 }} />
                                </a>
                            ) : (
                                <div className="d-inline-flex align-items-center gap-1 flex-wrap">
                                    <span className="fw-bold" style={{ fontSize: '0.98rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                                        {clientName}
                                    </span>
                                    {username && contactName && (
                                        <span className="fw-normal" style={{ fontSize: '0.80rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                                            (@{username})
                                        </span>
                                    )}
                                    {contactName && !ticket.idClienteAkasia && (
                                        <span
                                            className="badge px-2 py-0"
                                            style={{
                                                fontSize: '0.65rem',
                                                backgroundColor: isDark ? 'rgba(37, 211, 102, 0.15)' : '#ecfdf5',
                                                color: isDark ? '#34d399' : '#059669',
                                                border: isDark ? '1px solid rgba(52, 211, 153, 0.3)' : '1px solid #a7f3d0'
                                            }}
                                            title="Nombre del perfil de WhatsApp (Sin cliente Akasia asignado)"
                                        >
                                            Nombre WhatsApp
                                        </span>
                                    )}
                                </div>
                            )}

                            <span
                                className="badge px-2 py-1"
                                style={{
                                    fontSize: '0.74rem',
                                    fontWeight: 600,
                                    backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                                    color: isDark ? '#94a3b8' : '#64748b',
                                    border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
                                    borderRadius: '6px'
                                }}
                            >
                                #TCK-{ticketId}
                            </span>
                        </div>

                        {/* Badges secundarios (Nivel, Etiquetas, Reabierto) */}
                        <div className="d-flex align-items-center gap-1 gap-md-2 mb-2 flex-wrap">
                            {Boolean(ticket.bReabierto || ticket.isReopened) && (
                                <span
                                    className="badge rounded-pill d-inline-flex align-items-center gap-1"
                                    style={{
                                        backgroundColor: isDark ? 'rgba(236, 72, 153, 0.18)' : '#fdf2f8',
                                        color: isDark ? '#f472b6' : '#db2777',
                                        border: isDark ? '1px solid rgba(236, 72, 153, 0.35)' : '1px solid #fbcfe8',
                                        fontSize: '0.68rem',
                                        fontWeight: 700,
                                        padding: '2px 7px'
                                    }}
                                >
                                    <FontAwesomeIcon icon={faRotateRight} className="me-1" style={{ fontSize: '0.6rem' }} />
                                    Reabierto
                                </span>
                            )}

                            {ticket.idCatNivelSoporte ? (
                                <span
                                    className="badge rounded-pill d-inline-flex align-items-center gap-1"
                                    style={{
                                        backgroundColor: isDark ? 'rgba(59, 130, 246, 0.18)' : '#eff6ff',
                                        color: isDark ? '#93c5fd' : '#2563eb',
                                        border: isDark ? '1px solid rgba(59, 130, 246, 0.35)' : '1px solid #bfdbfe',
                                        fontSize: '0.68rem',
                                        fontWeight: 600,
                                        padding: '2px 7px'
                                    }}
                                >
                                    <FontAwesomeIcon icon={faLayerGroup} className="me-1" style={{ fontSize: '0.6rem' }} />
                                    {`Nivel ${ticket.idCatNivelSoporte}`}
                                </span>
                            ) : (
                                <span
                                    className="badge rounded-pill d-inline-flex align-items-center gap-1"
                                    style={{
                                        backgroundColor: isDark ? 'rgba(148, 163, 184, 0.12)' : '#f1f5f9',
                                        color: isDark ? '#94a3b8' : '#64748b',
                                        border: isDark ? '1px solid rgba(148, 163, 184, 0.25)' : '1px solid #e2e8f0',
                                        fontSize: '0.68rem',
                                        fontWeight: 600,
                                        padding: '2px 7px'
                                    }}
                                >
                                    <FontAwesomeIcon icon={faLayerGroup} className="me-1" style={{ fontSize: '0.6rem' }} />
                                    Sin nivel
                                </span>
                            )}

                            {ticket.vIncidencias && ticket.vIncidencias.split(',').map((incidencia, idx) => {
                                const label = incidencia.trim();
                                if (!label) return null;
                                return (
                                    <span
                                        key={idx}
                                        className="badge rounded-pill d-inline-flex align-items-center gap-1"
                                        style={{
                                            backgroundColor: isDark ? 'rgba(139, 92, 246, 0.18)' : '#f5f3ff',
                                            color: isDark ? '#c4b5fd' : '#7c3aed',
                                            border: isDark ? '1px solid rgba(139, 92, 246, 0.35)' : '1px solid #ddd6fe',
                                            fontSize: '0.68rem',
                                            fontWeight: 600,
                                            padding: '2px 7px'
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTag} className="me-1" style={{ fontSize: '0.6rem' }} />
                                        {label}
                                    </span>
                                );
                            })}

                            <div className="d-flex align-items-center gap-1 ms-auto ms-md-0" style={{ fontSize: '0.75rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                                <FontAwesomeIcon icon={isPhone ? faMobileAlt : faUser} className="me-1" style={{ fontSize: '0.7rem' }} />
                                <span>{phone}</span>
                            </div>
                        </div>

                        {/* Tiempos y Actividad */}
                        <div className="d-flex align-items-center gap-2 gap-md-3 mb-1 flex-wrap" style={{ fontSize: '0.75rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                            <div className="d-flex align-items-center gap-1">
                                <FontAwesomeIcon icon={faCommentDots} className="me-1" style={{ color: isUnread ? '#25d366' : '#10b981' }} />
                                <span className={isUnread ? 'fw-bold text-success' : ''}>
                                    Última actividad: {lastTime}
                                </span>
                            </div>

                            {timeFormatted && !isResolved && (
                                <div
                                    className="d-flex align-items-center gap-1 fw-medium"
                                    style={{
                                        color: isPending
                                            ? (isDark ? '#fb923c' : '#ea580c')
                                            : timeClass === 'danger'
                                                ? (isDark ? '#f87171' : '#dc2626')
                                                : timeClass === 'warning'
                                                    ? (isDark ? '#fbbf24' : '#d97706')
                                                    : (isDark ? '#38bdf8' : '#0284c7')
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={timeClass === 'danger' ? faTriangleExclamation : faClock}
                                        className="me-1"
                                        style={{ fontSize: '0.68rem' }}
                                    />
                                    <span>
                                        {isPending ? 'Sin contestar: ' : 'Tiempo de atención: '}
                                        <strong>{timeFormatted}</strong>
                                    </span>
                                </div>
                            )}

                            {isResolved && resolvedTime && (
                                <div
                                    className="d-flex align-items-center gap-1 fw-medium"
                                    style={{
                                        color: isDark ? '#34d399' : '#15803d'
                                    }}
                                    title="Tiempo de atención al cliente"
                                >
                                    <FontAwesomeIcon
                                        icon={faClock}
                                        className="me-1"
                                        style={{ fontSize: '0.68rem' }}
                                    />
                                    <span>
                                        Tiempo de atención: <strong>{resolvedTime}</strong>
                                    </span>
                                </div>
                            )}

                            {isResolved && firstResponseTime && (
                                <div
                                    className="d-flex align-items-center gap-1 fw-medium"
                                    style={{
                                        color: isDark ? '#38bdf8' : '#0284c7'
                                    }}
                                    title="Tiempo para la primera respuesta"
                                >
                                    <FontAwesomeIcon
                                        icon={faReply}
                                        className="me-1"
                                        style={{ fontSize: '0.68rem' }}
                                    />
                                    <span>
                                        1ra respuesta: <strong>{firstResponseTime}</strong>
                                    </span>
                                </div>
                            )}

                            {isResolved && closedDateTime && (
                                <div
                                    className="d-flex align-items-center gap-1 fw-medium"
                                    style={{
                                        color: isDark ? '#cbd5e1' : '#475569'
                                    }}
                                    title="Fecha y hora de cierre del ticket"
                                >
                                    <FontAwesomeIcon
                                        icon={faCalendarCheck}
                                        className="me-1"
                                        style={{ fontSize: '0.68rem', color: isDark ? '#34d399' : '#10b981' }}
                                    />
                                    <span>
                                        Cerrado: <strong>{closedDateTime}</strong>
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Asunto y Último mensaje */}
                        <p className="mb-0 lh-sm text-truncate mt-1" style={{ fontSize: '0.84rem' }}>
                            {ticket.vAsunto && (
                                <span
                                    className="badge border me-1"
                                    style={{
                                        backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                                        color: isDark ? '#38bdf8' : '#0284c7',
                                        borderColor: isDark ? '#334155' : '#e2e8f0',
                                        fontSize: '0.7rem'
                                    }}
                                >
                                    {ticket.vAsunto}
                                </span>
                            )}
                            <span
                                style={{
                                    color: isUnread
                                        ? (isDark ? '#f9fafd' : '#0f172a')
                                        : ticket.vUltimoMensaje
                                            ? (isDark ? '#cbd5e1' : '#475569')
                                            : (isDark ? '#64748b' : '#94a3b8'),
                                    fontStyle: ticket.vUltimoMensaje || ticket.vAsunto ? 'normal' : 'italic',
                                    fontWeight: isUnread ? 500 : 400
                                }}
                            >
                                {ticket.vUltimoMensaje ? formatWhatsAppText(ticket.vUltimoMensaje, { inlineOnly: true }) : (ticket.vAsunto ? '' : 'Sin mensajes')}
                            </span>
                        </p>
                    </div>

                    {/* 3. Columna de Acciones y Asesor */}
                    <div
                        className="d-flex flex-row flex-md-column align-items-center justify-content-end justify-content-md-between flex-shrink-0 ps-md-3 border-start-md pt-2 pt-md-0 gap-2 mt-2 mt-md-0"
                        style={{
                            minWidth: '140px',
                            borderColor: isDark ? '#233142' : '#eef2f6'
                        }}
                    >
                        {assignedAgentName && (
                            <div
                                className="d-flex align-items-center justify-content-center gap-1 px-2 py-1 rounded-2 flex-grow-1 flex-md-grow-0 text-truncate"
                                style={{
                                    backgroundColor: isDark ? 'rgba(5, 150, 105, 0.2)' : '#ecfdf5',
                                    color: isDark ? '#34d399' : '#065f46',
                                    border: isDark ? '1px solid rgba(52, 211, 153, 0.3)' : '1px solid #a7f3d0',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    maxWidth: '100%'
                                }}
                                title={`Asesor asignado: ${assignedAgentName}`}
                            >
                                <FontAwesomeIcon icon={faUserCheck} className="me-1" style={{ fontSize: '0.7rem' }} />
                                <span className="text-truncate">{assignedAgentName}</span>
                            </div>
                        )}

                        <div className="d-flex flex-row flex-md-column gap-2 w-100">
                            <a
                                href={ticketUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onSelectTicket(ticket);
                                }}
                                className="btn btn-sm text-white d-flex align-items-center justify-content-center gap-1 px-2 px-md-3 py-1 rounded-2 shadow-none w-100"
                                style={{
                                    backgroundColor: '#0886caff',
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    textDecoration: 'none'
                                }}
                            >
                                <FontAwesomeIcon icon={faEye} className="me-1" style={{ fontSize: '0.75rem' }} />
                                <span>Ver ticket</span>
                            </a>

                            {isPending && !hasAssignedAgent && (
                                <button
                                    type="button"
                                    onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (onAssignTicketAgent) {
                                            onAssignTicketAgent(ticket);
                                        }
                                    }}
                                    className="btn btn-sm text-white d-flex align-items-center justify-content-center gap-1 px-2 px-md-3 py-1 rounded-2 shadow-none w-100"
                                    style={{
                                        backgroundColor: '#059669',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        border: 'none'
                                    }}
                                >
                                    <FontAwesomeIcon icon={faUserCheck} className="me-1" style={{ fontSize: '0.75rem' }} />
                                    <span>Asignar Ticket</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TicketCardItem;
